import { Checkbox, Col } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useItemVariants } from "graphql/useItemExtras";
import { useTranslation } from "react-i18next";
import Items from "./Items/Items";
import s from "./styles";

function PricesPerVariant({ values, variantId, setFieldValue }) {
  const { t } = useTranslation();

  const [variantConfig, setVariantConfig] = useState(
    values.variantConfig ? values.variantConfig : []
  );

  useEffect(() => {
    if (!values.variantConfig) {
      setFieldValue("variantConfig", {
        pricePerVariant: false,
        variants: [],
      });
    }
  }, [values.variantConfig, setFieldValue]);

  useEffect(() => {
    setVariantConfig(values.variantConfig);
  }, [values.variantConfig]);

  const { itemVariants } = useItemVariants();
  const itemVariantsMap = useMemo(() => {
    const result = {};
    itemVariants.forEach(
      (itemVariant) => (result[itemVariant._id] = itemVariant)
    );
    return result;
  }, [itemVariants]);

  const items = values?.variantConfig?.variants || [];

  const variantCombinations = useMemo(() => {
    const itemVariant = itemVariantsMap[variantId.toString()];

    if (!itemVariant || !Array.isArray(itemVariant.variants)) return {};

    const combine = (arr) => {
      if (arr.length === 0) return [[]];
      const first = arr[0];
      const rest = combine(arr.slice(1));
      return first.items.flatMap((item) =>
        rest.map((combination) => [item, ...combination])
      );
    };

    const combinations = combine(itemVariant.variants);

    const result = {};

    const firstVariant = itemVariant.variants[0];
    firstVariant.items.forEach((item) => {
      result[item.value] = [item.name];
    });

    combinations.forEach((combination) => {
      if (combination.length > 1) {
        const key = combination.map((item) => item.value).join("");
        const values = combination.map((item) => item.name);
        result[key] = values;
      }
    });

    return result;
  }, [itemVariantsMap, variantId]);

  const handlePriceChange = (price, variant) => {
    const existingVariant = variantConfig.variants.find(
      (item) => item.variant === variant
    );

    if (existingVariant) {
      const updatedVariants = variantConfig.variants.map((item) =>
        item.variant === variant ? { ...item, price } : item
      );

      const filteredVariants = updatedVariants.filter((item) => item.price);

      setVariantConfig((prevConfig) => ({
        ...prevConfig,
        variants: filteredVariants,
      }));

      setFieldValue("variantConfig.variants", filteredVariants);
    } else {
      const newVariant = {
        variant,
        price,
        priceListId: null,
      };

      const updatedVariants = [...(variantConfig.variants || []), newVariant];

      setVariantConfig((prevConfig) => ({
        ...prevConfig,
        variants: updatedVariants,
      }));

      setFieldValue("variantConfig.variants", updatedVariants);
    }
  };

  return (
    <s.OptionsBoxContainer style={{ marginTop: "48px" }}>
      <s.OptionRowStock>
        <s.SlockSectionTitleHeader>
          {t(
            "components.InventoryItems.AddItem.PricesPerVariant.pricesPerVariant"
          )}
        </s.SlockSectionTitleHeader>
        <s.SlockSectionTitleHeader>
          <Checkbox
            name="hidden"
            onChange={(e) =>
              setFieldValue("variantConfig.pricePerVariant", e.target.checked)
            }
            checked={variantConfig?.pricePerVariant}
          >
            {t(
              "components.InventoryItems.AddItem.PricesPerVariant.enablePricesPerVariant"
            )}
          </Checkbox>
        </s.SlockSectionTitleHeader>
      </s.OptionRowStock>

      <s.OptionRow color="#F9F9F9">
        <Col span={9}>
          <s.OptionTextContainer>
            {t("components.InventoryItems.AddItem.PricesPerVariant.barCode")}
          </s.OptionTextContainer>
        </Col>
        <Col span={9}>
          <s.OptionTextContainer>
            {t("components.InventoryItems.AddItem.PricesPerVariant.item")}
          </s.OptionTextContainer>
        </Col>
        <Col span={6}>
          <s.OptionTextContainer>
            {t("components.InventoryItems.AddItem.PricesPerVariant.price")}
          </s.OptionTextContainer>
        </Col>
      </s.OptionRow>

      {variantConfig?.pricePerVariant &&
        variantCombinations &&
        Object.keys(variantCombinations).map((variant, index) => (
          <Items
            key={index}
            index={index}
            item={{ variant: variant }}
            items={items}
            values={values}
            placeholder={values.price}
            price={
              values.variantConfig?.variants?.find(
                (item) => item.variant === variant
              )?.price
            }
            itemVariant={variantId && itemVariantsMap[variantId.toString()]}
            onPriceChange={handlePriceChange}
          />
        ))}
    </s.OptionsBoxContainer>
  );
}

export default PricesPerVariant;
